<template>
  <BlockedSearchBanner
    v-if="isBlockedSearchBannerVisible"
    :dep-iata="searchParams.dep"
    :arr-iata="searchParams.arr"
    :search-date="searchParams.itineraries[0].date"
    :return-date="searchParams.itineraries[1]?.date"
    @onGoBack="backToMainPage"
  />
  <div
    v-else-if="search.isError.value"
    class="flex h-screen pb-4"
    style="padding-top: var(--status-bar-height)"
  >
    <AppError :title="$t('search.error.tryAgain')" class="p-4">
      <template #action>
        <FreeButton class="green-main-button w-full" @click="onInitError">
          {{ $t('search.error.backToMainPage') }}
        </FreeButton>
      </template>
    </AppError>
  </div>

  <div
    v-else-if="offerNotFoundError"
    class="flex h-screen pb-4"
    style="padding-top: var(--status-bar-height)"
  >
    <AppError :title="$t('search.error.offerNotFound')" class="p-4">
      <template #action>
        <FreeButton
          class="green-main-button w-full"
          @click="backToSearchResults"
        >
          {{ $t('search.error.backToSearchPage') }}
        </FreeButton>
      </template>
    </AppError>
  </div>
  <SearchResultsLoader
    v-else-if="search.isLoading.value && isInternationalSearch"
  >
    <template #back-button>
      <!-- Top location is calculated to be at the same position as back button in ResultsPage header -->
      <FreeBackButton
        class="absolute left-4 z-10"
        style="top: calc(var(--status-bar-height, 0px) + 0.5rem + 9px)"
        @click="backToMainPage"
      />
    </template>
  </SearchResultsLoader>
  <FreePageLayout v-else>
    <template #header>
      <SearchResultsHeaderShimmers
        v-if="search.isFetching.value"
        @onReturnButtonClick="backToMainPage"
      />
      <SearchResultsHeader
        v-else
        :dict="search.result.value.$meta.dict"
        :search-params="search.result.value.$meta.search_params"
        :is-favorite="isSearchFavorite"
        :is-favorite-disabled="!isAuthorized"
        :is-multi-trip-search="isMultiTripSearch"
        @onReturnButtonClick="backToMainPage"
        @onChangeParamsClick="openChangeSearchParamsBottomSheet"
        @onFavoriteToggle="toggleSearchFavorite"
      />
    </template>
    <template #default>
      <FavoritesOnboardingTooltip
        v-if="!isMultiTripSearch"
        ref="favoritesOnboarding"
      />
      <FavoriteAddedAlert
        v-if="!isMultiTripSearch"
        ref="favoriteAddedAlertRef"
      />
      <OneWayNearestSearches
        v-if="!isMultiTripSearch && searchParams.OW"
        class="px-2"
        :is-loading="search.isLoading.value"
        :selected-dep-date="searchParams.itineraries[0].date"
        :nearest-searches-data="nearestSearchesData"
        @onDateSelect="handleNewDatesSelect([$event])"
      />
      <template v-if="search.isLoading.value">
        <MiniFiltersShimmers /> <SearchTicketsListShimmers />
      </template>
      <template v-else>
        <RoundTripCalendarTrigger
          v-if="searchParams.RT"
          class="relative -left-4 w-screen"
        />
        <OfferFilters
          v-if="!search.isNoResults.value"
          class="bg-background-neutral-primary sticky z-30"
          :offers="search.offers.value"
          :dict="search.result.value.$meta.dict"
          @onOffersFilter="handleOffersFilter"
        >
          <template #offer-sort>
            <OfferSort
              :offers-length="search.offers.value.length"
              :sort-mode="sortMode"
              @onChangeSortMode="handleOffersSort"
            />
          </template>
        </OfferFilters>
        <NoSearchResults
          v-if="search.isNoResults.value"
          :hash-code="hashCode"
          @onDatesSelect="handleNoOffersDatesSelect"
          @onSearchFormSubmit="handleNoOffersSearchFormSubmit"
        />
        <div v-else class="mb-4">
          <BloomreachBanner
            class="mb-4"
            :placement="BannerPlacement.SERP_PAGE_OPEN"
          />
          <DirectFlightsBlock
            v-if="!isMultiTripSearch && shouldShowDirectFlights"
            class="mb-4"
            :offers="filteredDirectFlights"
            :dict="search.result.value.$meta.dict"
            @onGroupClick="handleDirectFlightGroupClick"
          />
          <SearchTicketsList
            :start-idx="Number(shouldShowDirectFlights)"
            :dict="search.result.value.$meta.dict"
            :offers-list="filteredOffers"
            :is-recommended-sorting-mode="isRecommendedSortingMode"
            :use-ranking-offer-labels="useRankingOfferLabels"
            :ranking="search.result.value.ranking"
            :offer-id="scrollToOfferId"
            :search-query="hashCode"
            :is-multi-trip-search="isMultiTripSearch"
            @onOfferSelect="handleOfferSelect"
            @onScrollToOfferEnd="clearScrollToOffer"
          />
          <SearchUnfilteredTicketsList
            v-if="isFilterActive"
            :dict="search.result.value.$meta.dict"
            :offers="unfilteredOffers"
            :is-recommended-sorting-mode="isRecommendedSortingMode"
            :use-ranking-offer-labels="useRankingOfferLabels"
            :ranking="search.result.value.ranking"
            :start-idx="filteredOffers.length"
            :offer-id="scrollToOfferId"
            :search-query="hashCode"
            :is-multi-trip-search="isMultiTripSearch"
            @onOfferSelect="
              (offer, index) =>
                handleOfferSelect(offer, index + filteredOffers.length)
            "
            @onScrollToOfferEnd="clearScrollToOffer"
          />
        </div>
        <RoundTripSearchesBottomSheet
          v-if="searchParams.RT"
          :search-query="hashCode"
          :search-params="searchParams"
          :dict="search.result.value.$meta.dict"
          :bottom-sheet-name="rtBottomSheet.name"
          @onDatesSelect="handleNewDatesSelect($event)"
        />
      </template>
      <ChangeParamsBottomSheet
        v-if="!search.isFetching.value"
        :name="CHANGE_PARAMS_BOTTOM_SHEET_NAME"
        @onSearchFormSubmit="
          (query, iata) => startNewSearch(query, undefined, true, iata)
        "
      />
    </template>
  </FreePageLayout>
</template>

<script setup lang="ts">
  import {
    computed,
    onBeforeMount,
    ref,
    watch,
    nextTick,
    useTemplateRef,
  } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import {
    FreeBackButton,
    FreeButton,
    FreePageLayout,
    AppError,
    freedomCashbackModel,
    OneWayNearestSearches,
    amplitudeExperiment,
    flightEntity,
  } from 'freedom-shared';
  import { controllerModel, hashCodeLib, citySearchHistory } from 'shared';
  import type { AxiosError } from 'axios';
  import type { Dict } from '@aviata/platform-types';

  import {
    RoundTripCalendarTrigger,
    RoundTripSearchesBottomSheet,
    roundTripSearchesBottomSheetController,
  } from '@/widgets/nearest-searches-rt';
  import { OfferFilters } from '@/widgets/offer-filters';
  import { MiniFiltersShimmers } from '@/widgets/offer-filters/ui';
  import {
    DirectFlightsBlock,
    directFlightsLib,
    useDirectFlightsStore,
    type directFlightsTypes,
  } from '@/widgets/direct-flights';

  import { searchTicketsModel } from '@/features/search-tickets';
  import {
    DEFAULT_SORT_MODE,
    OfferSort,
    offerSortModel,
    type SortMode,
  } from '@/features/offer-sort';
  import { searchApi } from '@/features/search';
  import {
    BlockedSearchBanner,
    useBlockedSearchBanner,
  } from '@/features/blocked-search-banner';
  import { FavoritesOnboardingTooltip } from '@/features/favorites-onboarding';
  import {
    FavoriteAddedAlert,
    FAVORITE_WATCHLIST_KIND,
    useWatchlist,
  } from '@/features/favorites';
  import { isQuickBookingEligible } from '@/features/quick-booking/lib/eligibility';
  import {
    QUICK_BOOKING_VARIANT,
    useQuickBookingExperiment,
  } from '@/features/quick-booking/model/use-quick-booking-experiment';

  import { BloomreachBanner } from '@/entities/bloomreach-banner';
  import {
    lastSearchModel,
    saveLastSearchFromDict,
  } from '@/entities/last-search';
  import { useNearestSearches } from '@/entities/nearest-searches';

  import { sentryModel } from '@/shared/model';
  import {
    isInternationalCity,
    analyticsEventsLib,
    amplitudeEventsLib,
    extractAnalyticsDataLib,
    isDirectFlight,
    BannerPlacement,
  } from '@/shared/lib';
  import { api } from '@/shared/api';
  import {
    amplitudeEventsConfig,
    type amplitudeEventsConfigTypes,
  } from '@/shared/config';

  import {
    SearchResultsHeader,
    SearchTicketsList,
    NoSearchResults,
    SearchResultsLoader,
    SearchResultsHeaderShimmers,
    SearchUnfilteredTicketsList,
    SearchTicketsListShimmers,
    ChangeParamsBottomSheet,
  } from './ui';

  const props = defineProps<{ hashCode: string }>();

  const emit = defineEmits<{
    onOfferSelect: [offer: flightEntity.types.MappedOffer];
    onSuccessfulSearch: [
      dict: Dict.Entity,
      resultId: string,
      dataForAnalytics: analyticsEventsLib.SearchPageOpenParams,
    ];
    onFailedSearch: [dataForAnalytics: analyticsEventsLib.SearchPageOpenParams];
  }>();

  const rtBottomSheet = roundTripSearchesBottomSheetController();

  const router = useRouter();
  const route = useRoute();
  const bottomSheet = controllerModel.useBottomSheet();
  const CHANGE_PARAMS_BOTTOM_SHEET_NAME = 'change-params-bottom-sheet';

  const favoritesOnboarding = useTemplateRef('favoritesOnboarding');
  const favoriteAddedAlertRef = useTemplateRef<
    InstanceType<typeof FavoriteAddedAlert>
  >('favoriteAddedAlertRef');

  const { favoriteSearchQueries, toggleSearch, isAuthorized } = useWatchlist();

  const isSearchFavorite = computed(() =>
    favoriteSearchQueries.value.has(props.hashCode),
  );

  const search = searchTicketsModel.useSearch(searchApi.startSearch);
  const searchParams = computed(() =>
    hashCodeLib.getSearchParams(props.hashCode),
  );
  /**
   * Multi-trip (multi-city) search has 3 or 4 itineraries (e.g. ALA-NQZ-CIT-SCO).
   * Used to hide functionality that is not yet adapted for multi-trip
   * (favorites, nearest-searches, direct-flights block).
   */
  const isMultiTripSearch = computed(() =>
    hashCodeLib.isMultiTripSearch(props.hashCode),
  );
  const searchHistory =
    citySearchHistory.model.useCitySearchHistory('free-avia');

  const toggleSearchFavorite = async () => {
    const offers = search.offers.value;
    if (!offers.length) {
      return;
    }
    const cheapestOffer = offers.reduce((min, offer) =>
      Number(offer.price.amount) < Number(min.price.amount) ? offer : min,
    );
    const result = await toggleSearch(
      props.hashCode,
      cheapestOffer.price.amount,
      cheapestOffer.price.currency,
    );
    if (result === 'added') {
      amplitudeEventsLib.addToFavorites('serp_page');
      favoriteAddedAlertRef.value?.show(FAVORITE_WATCHLIST_KIND.SEARCH);
    } else if (result === 'removed') {
      amplitudeEventsLib.removeFromFavorites('serp_page');
    } else if (result === 'limit_reached') {
      favoriteAddedAlertRef.value?.showLimitError();
    }
  };

  const { isVisible: isBlockedSearchBannerVisible } = useBlockedSearchBanner({
    depIata: computed(() => searchParams.value.dep),
    arrIata: computed(() => searchParams.value.arr),
    searchDate: computed(() => searchParams.value.itineraries[0].date),
    returnDate: computed(() => searchParams.value.itineraries[1]?.date),
  });

  const filteredOffers = ref<flightEntity.types.MappedOffer[]>([]);

  const { sortOffers, sortMode, changeSortMode } = offerSortModel.useOfferSort({
    ranking: computed(() => search.result.value.ranking),
  });

  const useRankingOfferLabels = computed(() =>
    Boolean(search.result.value.ranking?.length),
  );

  const directFlightsStore = useDirectFlightsStore();

  const quickBookingExperiment = useQuickBookingExperiment();

  const { isVariant: isNewBottomNavVariant } =
    amplitudeExperiment.lib.checkExperimentVariant(
      amplitudeExperiment.config.flags.NEW_BOTTOM_NAV,
      'bottom_nav_bar',
    );

  const shouldShowDirectFlights = computed(() => directFlightsStore.hasGroups);

  const filteredDirectFlights = computed(() =>
    filteredOffers.value.filter(isDirectFlight),
  );

  watch(filteredDirectFlights, offers => directFlightsStore.setOffers(offers), {
    immediate: true,
  });

  const isRecommendedSortingMode = computed(
    () => sortMode.value === offerSortModel.SORT_MODES.RECOMMENDED,
  );

  const unfilteredOffers = computed(() => {
    const filteredOfferIds = new Set(filteredOffers.value.map(o => o.id));

    const result = search.offers.value.filter(
      offer => !filteredOfferIds.has(offer.id),
    );

    return sortOffers({ offers: result });
  });

  const isFilterActive = ref(false);

  const offerNotFoundError = ref(false);
  const scrollToOfferId = ref<string | undefined>(undefined);
  const clearScrollToOffer = () => {
    scrollToOfferId.value = undefined;
  };

  const sharedOfferHashCode = computed(
    () => route.query.offerHash as string | undefined,
  );

  watch(
    () => filteredOffers.value,
    newOffers => {
      isFilterActive.value = !(
        newOffers.length === search.offers.value.length &&
        newOffers.length !== 0
      );
    },
  );

  const scrollToTop = () => {
    /**
     * For some reason it didn't scroll every time it was called.
     * nexttick seems to solve the problem.
     * */
    nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  const hasResultsFilter = ref<boolean>(false);
  const resultFilterParams = ref<Record<string, unknown>>({});

  const handleOffersFilter = (
    newFilteredOffers: flightEntity.types.MappedOffer[],
    filterParams: Record<string, unknown>,
    hasFilter: boolean,
  ) => {
    hasResultsFilter.value = hasFilter;
    resultFilterParams.value = filterParams;
    filteredOffers.value = sortOffers({ offers: newFilteredOffers });
    scrollToTop();
  };

  const handleOffersSort = (sortMode: SortMode) => {
    changeSortMode(sortMode);
    filteredOffers.value = sortOffers({ offers: filteredOffers.value });
    scrollToTop();
  };

  const handleOfferSelect = (
    offer: flightEntity.types.MappedOffer,
    index: number,
    isFromDirectFlights?: boolean,
  ) => {
    // Determine source: use DIRECT_FLIGHTS if from direct flights block,
    // otherwise use route source
    let source: amplitudeEventsConfigTypes.DetailsPageOpenSource;
    if (isFromDirectFlights) {
      directFlightsStore.setIsOfferFromDirectFlights(true);
      source = amplitudeEventsConfig.DETAILS_PAGE_OPEN_SOURCE.DIRECT_FLIGHTS;
    } else {
      directFlightsStore.setIsOfferFromDirectFlights(false);
      source = route.params.source as amplitudeEventsConfigTypes.SERPOpenSource;
    }

    if (isDirectFlight(offer)) {
      const airline = directFlightsLib.getOfferAirline(
        offer,
        searchParams.value.RT,
      );
      if (airline) {
        directFlightsStore.selectAirline(airline);
      }
    }

    const rankingTagsForLabel =
      useRankingOfferLabels.value && search.result.value.ranking?.length
        ? (search.result.value.ranking.find(r => r.offer_id === offer.id)
            ?.tags ?? [])
        : undefined;

    analyticsEventsLib.detailsPageOpen({
      offer,
      index,
      source,
      hasFilter: hasResultsFilter.value,
      dict: search.result.value.$meta.dict,
      searchQuery: props.hashCode,
      sortType: sortMode.value,
      rankingTagsForLabel,
    });

    // QuickBooking AB-test: log assignment for every eligible click on
    // any variant (CLASSIC included) so the three arms share one funnel
    // entry point in Amplitude. The `variant` property on the event lets
    // a per-arm funnel be built downstream. The actual routing decision
    // below picks between the QuickBookingPage shortcut and OfferDetails.
    const variant = quickBookingExperiment.variant.value;
    const isEligibleForQuickBooking = isQuickBookingEligible(
      offer,
      searchParams.value,
    );
    if (isEligibleForQuickBooking && variant) {
      const sp = searchParams.value;
      amplitudeEventsLib.quickBookingAssigned({
        variant,
        offerId: offer.id,
        providerClass: offer.$meta?.provider_class ?? '',
        tripType: sp.OW ? 'OW' : 'RT',
        paxCount:
          sp.passengers.adt +
          sp.passengers.chd +
          sp.passengers.inf +
          sp.passengers.yth,
        paxBreakdown: {
          adt: sp.passengers.adt,
          chd: sp.passengers.chd,
          inf: sp.passengers.inf,
          yth: sp.passengers.yth,
        },
        isDomestic: Boolean(offer.$meta?.is_domestic),
      });
    }

    // IMMEDIATE variant skips OfferDetails entirely, EXCEPT when the user
    // arrived from the direct-flights block — that block expects the
    // OfferDetails airline/time picker, so we keep that detour and let the
    // OfferDetails "Continue" handler route into QuickBookingPage afterwards.
    if (
      isEligibleForQuickBooking &&
      variant === QUICK_BOOKING_VARIANT.IMMEDIATE &&
      !isFromDirectFlights &&
      search.result.value?.id
    ) {
      emit('onOfferSelect', offer);
      router.push({
        name: 'QuickBookingPage',
        params: {
          resultId: search.result.value.id,
          offerId: offer.id,
          source,
        },
        query: {
          back: route.query.back === 'chat' ? 'chat' : undefined,
        },
      });
      return;
    }

    router.push({
      name: 'OfferDetails',
      params: { offerId: offer.id, source },
      query: {
        back: route.query.back === 'chat' ? 'chat' : undefined,
      },
    });

    emit('onOfferSelect', offer);
  };

  const handleDirectFlightGroupClick = (
    group: directFlightsTypes.DirectFlightGroup,
  ) => {
    if (!group.offers.length) return;

    // Track direct flight click event (no time chosen)
    analyticsEventsLib.directFlightsClick({
      airline_choosen: group.airline.iata,
    });

    const cheapestOffer = directFlightsLib.findCheapestOfferFromGroup(
      group,
      searchParams.value.RT,
    );
    directFlightsStore.selectAirline(group.airline);

    if (cheapestOffer) {
      handleOfferSelect(cheapestOffer, 0, true);
    }
  };

  const findOfferByHashCode = (offerHashCode: string) => {
    const index = search.offers.value.findIndex(
      offer => offer.$meta?.hash === offerHashCode,
    );

    if (index === -1) {
      return null;
    }

    return {
      offer: search.offers.value[index],
      index,
    };
  };

  const navigateToSharedOffer = () => {
    if (!sharedOfferHashCode.value) return;

    const result = findOfferByHashCode(sharedOfferHashCode.value);

    if (result && search.result.value?.id) {
      handleOfferSelect(result.offer, result.index);
    } else {
      // Offer not found
      offerNotFoundError.value = true;

      sentryModel.withScope(scope => {
        scope.setLevel(sentryModel.level.WARNING);
        sentryModel?.captureMessage('Shared offer not found after search', {
          extra: {
            searchHashCode: props.hashCode,
            offerHashCode: sharedOfferHashCode.value,
            offersCount: search.offers.value.length,
            resultId: search.result.value?.id,
          },
        });
      });
    }
  };

  const scrollToDeepLinkOffer = () => {
    if (!route.query.offerHash) {
      return;
    }

    const result = findOfferByHashCode(String(route.query.offerHash));

    if (!result) {
      return;
    }

    scrollToOfferId.value = result.offer.id;
  };

  const backToSearchResults = () => {
    offerNotFoundError.value = false;
    router.replace({
      name: 'OffersResults',
      params: {
        hashCode: props.hashCode,
        source: route.params.source,
      },
    });
  };

  const startSearch = (
    hashCode: string,
    source?: amplitudeEventsConfigTypes.SERPOpenSource,
    isFromChangeParamsBottomSheet?: boolean,
  ) => {
    const searchStartTime = Date.now();

    const searchSource =
      source ??
      (route.params.source as amplitudeEventsConfigTypes.SERPOpenSource);

    search
      .startAirSearch(hashCode)
      .then(() => {
        const dataForAnalytics = extractAnalyticsDataLib.prepareSearchAnalytics(
          {
            success: true,
            offers: search.result.value.items,
            searchStartTime,
            hasFilter: hasResultsFilter.value,
            filterParams: resultFilterParams.value,
            partnerFilter: search.result.value.$meta.partner_filter,
            segmentFilter: search.result.value.$meta.segment_filter_on,
            calendarFilter: !!route.query.iata,
            source: isFromChangeParamsBottomSheet
              ? amplitudeEventsConfig.SERP_OPEN_SOURCE.SEARCH_PAGE
              : searchSource,
            searchParams: searchParams.value,
            dict: search.result.value.$meta.dict,
          },
        );

        saveLastSearchFromDict(search.result.value.$meta.dict, props.hashCode);

        emit(
          'onSuccessfulSearch',
          search.result.value.$meta.dict,
          search.result.value.id,
          dataForAnalytics,
        );

        favoritesOnboarding.value?.start();

        if (sharedOfferHashCode.value) {
          if (route.params.source === 'deep_link') {
            scrollToDeepLinkOffer();
          }

          nextTick(() => {
            navigateToSharedOffer();
          });
        }
      })
      .catch((error: AxiosError<Record<string, unknown>>) => {
        sentryModel.withScope(scope => {
          scope.setLevel(sentryModel.level.FATAL);
          sentryModel?.captureException(
            sentryModel.getReadableError(
              sentryModel.createCustomErrorText(
                sentryModel.errorPrefix.DEFAULT,
                'TO_START_SEARCH',
              ),
              error,
            ),
            {
              extra: {
                errors: error.response?.data?.errors,
                hashcode: hashCode,
                offerHash: sharedOfferHashCode.value,
              },
            },
          );
        });

        const dataForAnalytics = extractAnalyticsDataLib.prepareSearchAnalytics(
          {
            success: false,
            searchStartTime,
            hasFilter: hasResultsFilter.value,
            filterParams: resultFilterParams.value,
            calendarFilter: !!route.query.iata,
            source: isFromChangeParamsBottomSheet
              ? amplitudeEventsConfig.SERP_OPEN_SOURCE.SEARCH_PAGE
              : searchSource,
            searchParams: searchParams.value,
            error: error.response?.data?.errors[0]?.message,
          },
        );

        emit('onFailedSearch', dataForAnalytics);
      });
  };

  const startNewSearch = (
    newHashCode: string,
    source?: amplitudeEventsConfigTypes.SERPOpenSource,
    isFromChangeParamsBottomSheet?: boolean,
    airlineIata?: string,
  ) => {
    const routeSource = isFromChangeParamsBottomSheet
      ? amplitudeEventsConfig.SERP_OPEN_SOURCE.SEARCH_PAGE
      : (source ?? route.params.source);

    router.replace({
      name: 'OffersResults',
      params: {
        hashCode: newHashCode,
        source: routeSource,
      },
      query: airlineIata ? { iata: airlineIata } : undefined,
    });

    startSearch(newHashCode, source, isFromChangeParamsBottomSheet);
  };

  /** Need to navigate to main page with page reload */
  const onInitError = () => {
    const { href } = router.resolve({ name: 'MainPage' });
    window.location.replace(href);
  };

  const backToMainPage = () => {
    const historyState = window.history.state;
    const previousPath = historyState?.back as string | undefined;

    if (
      previousPath?.includes('/search/') ||
      previousPath?.includes('/favorites')
    ) {
      router.back();
      return;
    }

    /**
     * When the user enters the app via a deeplink to SERP (search or
     * deeplink-with-offer-hash from native Freedom Travel) the SPA history
     * has only this entry. SPA-level `router.replace` to MainPage does not
     * always propagate the URL change to the Freedom Superapp webview
     * host — the user lands on a blank screen. Force a top-level reload,
     * mirroring the `onInitError` pattern above, so the host sees the
     * actual URL change and the SPA re-initialises cleanly.
     */
    const { href } = router.resolve({
      name: 'MainPage',
      query: {
        source: 'search_page',
        sourceElement: 'back_button',
      },
    });
    window.location.replace(href);
  };

  /**
   * Update dates in localStorage last search params
   */
  const updateLastSearchDates = (newDates: string[]) => {
    const lastSearchParams = lastSearchModel.lastSearch.getSavedSearchParams();
    if (!lastSearchParams) return;

    const firstRoute = lastSearchParams.routes[0];
    let updatedRoutes;

    if (newDates.length === 1) {
      // One way: keep only first route with new date
      updatedRoutes = [
        {
          ...firstRoute,
          dates: [newDates[0]],
        },
      ];
    } else {
      updatedRoutes = [
        {
          ...firstRoute,
          dates: newDates,
        },
      ];
    }

    lastSearchModel.lastSearch.saveSearchParams({
      ...lastSearchParams,
      routes: updatedRoutes,
    });
  };

  const handleNoOffersDatesSelect = (
    newDates: string[],
    airlineIata?: string,
  ) => {
    updateLastSearchDates(newDates);
    const query = hashCodeLib.buildNewHashCode(props.hashCode, newDates);
    startNewSearch(query, undefined, false, airlineIata);
  };

  const handleNoOffersSearchFormSubmit = (
    newQuery: string,
    airlineIata?: string,
  ) => {
    startNewSearch(newQuery, undefined, false, airlineIata);
  };

  const handleNewDatesSelect = (newDates: string[]) => {
    // Update dates in localStorage
    updateLastSearchDates(newDates);

    const query = hashCodeLib.buildNewHashCode(props.hashCode, newDates);
    changeSortMode(DEFAULT_SORT_MODE);
    startNewSearch(
      query,
      amplitudeEventsConfig.SERP_OPEN_SOURCE.CALENDAR_PRICE,
    );
  };

  const isInternationalSearch = computed(() => {
    const { dep, arr } = searchParams.value;
    const cachedCities = searchHistory.getCities();
    const depCity = cachedCities.find(c => c.code === dep);
    const arrCity = cachedCities.find(c => c.code === arr);

    if (depCity || arrCity) {
      return isInternationalCity(depCity) || isInternationalCity(arrCity);
    }

    const lastSearchParams = lastSearchModel.lastSearch.getSavedSearchParams();
    if (!lastSearchParams) return false;

    return (
      isInternationalCity(lastSearchParams.routes[0].dep) ||
      isInternationalCity(lastSearchParams.routes[0].arr)
    );
  });

  const openChangeSearchParamsBottomSheet = () => {
    analyticsEventsLib.openChangeSearchParams();
    bottomSheet.show(CHANGE_PARAMS_BOTTOM_SHEET_NAME);
  };

  const nearestSearchesData = useNearestSearches();

  watch(
    () => props.hashCode,
    newSearchQuery => {
      nearestSearchesData.init(newSearchQuery);
    },
    { immediate: true },
  );

  // Update round-trip status when search params change
  watch(
    () => searchParams.value.RT,
    isRT => {
      directFlightsStore.setIsRoundTrip(isRT);
    },
    { immediate: true },
  );

  watch(
    () => search.offers.value,
    newOffers => (filteredOffers.value = newOffers),
  );

  onBeforeMount(() => {
    if (isBlockedSearchBannerVisible.value) return;

    startSearch(props.hashCode);
    freedomCashbackModel.prefetchFreedomCashback({ pfm: api });
  });
</script>
